import React, { useState } from 'react';
import {
  Typography, TextField, Button,
  Checkbox, FormControlLabel, Alert, Box
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import useMessages from '../../../hooks/useMessages';
import FormInputMUI from '../../commun/FormInputMUI';
import FormLayout from '../../commun/FormLayout';
import Footer from '../../commun/Footer';
import './signup.css';

// Icônes
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import PhoneInput from 'react-phone-input-2';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const FormEntreprise = () => {
  const navigate = useNavigate();
  const { messages, handleSuccess, handleError } = useMessages();

  const [formData, setFormData] = useState({
    NomEntreprise: '', lastName: '', firstName: '',
    email: '', phone: '', motDePasse: '',
    cguAccepted: false, verificationCode: '',
  });

  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(true);
  const [showVerification, setShowVerification] = useState(false);
  const [entrepriseExistante, setEntrepriseExistante] = useState(null);
  const [entrepriseCheckMessage, setEntrepriseCheckMessage] = useState('');

  const setField = (name, value) =>
    setFormData(prev => ({ ...prev, [name]: value }));

  const validate = () => {
    const newErrors = {};
    if (!formData.NomEntreprise) newErrors.NomEntreprise = 'Champ requis.';
    if (!formData.lastName) newErrors.lastName = 'Champ requis.';
    if (!formData.firstName) newErrors.firstName = 'Champ requis.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Email invalide.';
    if (!/^\+?[0-9]{10,15}$/.test(formData.phone))
      newErrors.phone = 'Téléphone invalide.';
    if (!formData.motDePasse || formData.motDePasse.length < 8)
      newErrors.motDePasse = 'Min. 8 caractères.';
    if (!formData.cguAccepted) newErrors.cguAccepted = 'CGU obligatoire.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return handleError('Veuillez remplir tous les champs requis.');

    try {
      const check = await axios.get(`${API_BASE}/api/entreprise/check-exist`, {
        params: { name: formData.NomEntreprise }
      });

      const endpoint = check.data.exists
        ? '/api/auth/signup-entreprise-existante'
        : '/api/auth/signup-entreprise';

      await axios.post(`${API_BASE}${endpoint}`, formData);

      handleSuccess('Inscription réussie. Code envoyé.');
      setShowForm(false);
      setShowVerification(true);
    } catch (err) {
      handleError(err.response?.data?.message || "Erreur d'inscription.");
    }
  };

  const handleVerification = async e => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_BASE}/api/auth/verify`, {
        email: formData.email,
        verificationCode: formData.verificationCode,
      });

      handleSuccess(res.data.message || 'Compte vérifié avec succès.');
      setTimeout(() => navigate('/login'), 5000);
    } catch (err) {
      const msg = err.response?.data?.message || 'Code incorrect.';
      handleError(msg);
    }
  };

  const handleNomEntrepriseChange = async (value) => {
    setField('NomEntreprise', value);
    try {
      const res = await axios.get(`${API_BASE}/api/entreprise/check-exist`, {
        params: { name: value }
      });

      setEntrepriseExistante(res.data.exists ? res.data.entreprise : null);
      setEntrepriseCheckMessage(
        res.data.exists
          ? "Entreprise déjà existante, votre compte sera rattaché à celle-ci."
          : "Nouvelle entreprise détectée. Elle sera soumise à validation par notre équipe."
      );
    } catch (err) {
      console.error("Erreur de vérification entreprise", err);
      setEntrepriseCheckMessage("Erreur lors de la vérification de l'entreprise.");
    }
  };

  return (
    <>
      <FormLayout
        infoTitle="	Trouvez les bons talents IT plus rapidement"
        infoSubtitle="Confiez vos besoins à notre réseau de recruteurs experts"
        infoPoints={[
          "✓ Déposez vos besoins en quelques clics",
          "✓ Recevez des profils qualifiés de recruteurs experts",
          "✓ Suivi en temps réel et évaluation des recruteurs"
        ]}
      >
        <Typography variant="h4" align="center" gutterBottom>
          Inscription Entreprise
        </Typography>

        {messages.error && <Alert severity="error">{messages.error}</Alert>}
        {messages.success && <Alert severity="success">{messages.success}</Alert>}

        {showForm && (
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
              gap={2}
            >
              <Box gridColumn="span 2">
                <FormInputMUI
                  label="Nom de l'entreprise *"
                  value={formData.NomEntreprise}
                  onChange={handleNomEntrepriseChange}
                  error={errors.NomEntreprise}
                  icon={<BusinessIcon fontSize="small" />}
                />
                {entrepriseCheckMessage && (
                  <Typography
                    variant="body2"
                    sx={{ color: entrepriseExistante ? 'green' : 'orange', mt: 1 }}
                  >
                    {entrepriseCheckMessage}
                  </Typography>
                )}
              </Box>

              <FormInputMUI
                label="Nom *"
                value={formData.lastName}
                onChange={(val) => setField('lastName', val)}
                error={errors.lastName}
                icon={<PersonIcon fontSize="small" />}
              />
              <FormInputMUI
                label="Prénom *"
                value={formData.firstName}
                onChange={(val) => setField('firstName', val)}
                error={errors.firstName}
                icon={<PersonIcon fontSize="small" />}
              />
              <FormInputMUI
                label="Email *"
                type="email"
                value={formData.email}
                onChange={(val) => setField('email', val)}
                error={errors.email}
                icon={<EmailIcon fontSize="small" />}
              />
              <Box>
                <PhoneInput
                  country="fr"
                  onlyCountries={['fr', 'ma', 'tn']}
                  value={formData.phone}
                  onChange={(value) => {
                    const fullNumber = `+${value}`;
                    setField('phone', fullNumber);
                  }}
                  inputStyle={{ width: '100%', height: 50 }}
                />

                {(errors.phone) && (
                  <p style={{ color: 'red' }}>{errors.phone}</p>
                )}
              </Box>
              <Box gridColumn="span 2">
                <FormInputMUI
                  label="Mot de passe *"
                  type="password"
                  value={formData.motDePasse}
                  onChange={(val) => setField('motDePasse', val)}
                  error={errors.motDePasse}
                  icon={<LockIcon fontSize="small" />}
                />
              </Box>
              <Box gridColumn="span 2">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.cguAccepted}
                      onChange={(e) => setField('cguAccepted', e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      J'accepte les&nbsp;
                      <a href="/cgu" target="_blank" rel="noopener noreferrer">CGU</a> *
                    </Typography>
                  }
                />
                {errors.cguAccepted && (
                  <Typography className="cgu-error">{errors.cguAccepted}</Typography>
                )}
              </Box>
              <Box gridColumn="span 2">
                <Button type="submit" variant="contained" fullWidth>
                  Confirmer
                </Button>
              </Box>
            </Box>
          </form>
        )}

        {showVerification && (
          <form onSubmit={handleVerification} className="verif-wrap">
            <TextField
              label="Code de vérification"
              fullWidth
              required
              value={formData.verificationCode}
              onChange={e => setField('verificationCode', e.target.value)}
              margin="normal"
            />
            <Button variant="outlined" type="submit" fullWidth>
              Vérifier le compte
            </Button>
          </form>
        )}
      </FormLayout>
      <Footer />
    </>
  );
};

export default FormEntreprise;
